import React, { useEffect, useState } from 'react';
import {
  Gavel, FileText, Calendar, CheckCircle2, AlertCircle,
  ShieldAlert, Clock, Hammer, Package,
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import WorkflowBuilder from './WorkflowBuilder';
import workflowsService from '../services/workflowsService';
import HelpLink from '../components/HelpLink';

// Modal: start a configured workflow (e.g. the operator's lien process)
// against a delinquent rental. Template comes from the facility's workflow
// builder; total due defaults to the rental balance and is exposed to the
// workflow as the {{total_due}} merge pill.
const StartWorkflowModal = ({ facilityId, row, onClose, onStarted }) => {
  const [templates, setTemplates] = React.useState([]);
  const [templateId, setTemplateId] = React.useState('');
  const [totalDue, setTotalDue] = React.useState(row.balance ? Number(row.balance).toFixed(2) : '');
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    workflowsService.listTemplates(facilityId)
      .then(({ data }) => {
        setTemplates(data);
        if (data.length === 1) setTemplateId(data[0]._id);
      })
      .catch(() => setTemplates([]));
  }, [facilityId]);

  const start = async () => {
    setBusy(true); setError(null);
    try {
      await workflowsService.startRun(facilityId, {
        template_id: templateId,
        customer_id: row.customer_id,
        rental_id: row.id,
        context: totalDue ? { total_due: parseFloat(totalDue) } : {},
      });
      onStarted();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start the workflow.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-[15px] font-semibold text-slate-900 mb-1">
          Start workflow for {row.customer_name}
        </h3>
        <p className="text-[12px] text-slate-400 mb-4">Unit {row.unit_number} &middot; balance ${Number(row.balance || 0).toFixed(2)}</p>
        {templates.length === 0 ? (
          <p className="text-[13px] text-slate-600 mb-4">
            No workflows configured yet — build one first in the
            <strong> Workflows</strong> tab (e.g. your lien process), then start it from here.
          </p>
        ) : (
          <div className="space-y-3 mb-4">
            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Workflow</label>
              <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]">
                <option value="">Select a workflow…</option>
                {templates.map((t) => <option key={t._id} value={t._id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[12px] font-medium text-slate-500 mb-1">Total due ($) — fills the {'{{total_due}}'} field on notices</label>
              <input type="number" step="0.01" min="0" value={totalDue}
                onChange={(e) => setTotalDue(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]" />
            </div>
          </div>
        )}
        {error && <p className="text-[12px] text-red-500 mb-3">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose}
            className="px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-600 hover:bg-slate-300">
            Cancel
          </button>
          {templates.length > 0 && (
            <button type="button" onClick={start} disabled={!templateId || busy}
              className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
              {busy ? 'Starting…' : 'Start workflow'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Auctions -- consolidated lien / auction / unit-recovery surface.
// Tonight (2026-06-05) is a UI-only MVP. Backend lien workflow (pre-lien
// certified-mail step, lien notice escalation, auction scheduling,
// proceeds disbursement) lands in a follow-up. For now the page shows
// the operator their delinquent rentals + a 5-stage explainer so they
// know where in the cascade each tenant sits once the engine ships.
const LIFECYCLE_STAGES = [
  { key: 'pre_lien', label: 'Pre-lien Notice', icon: AlertCircle },
  { key: 'lien_notice', label: 'Lien Notice', icon: ShieldAlert },
  { key: 'auction_scheduled', label: 'Auction Scheduled', icon: Clock },
  { key: 'auction_held', label: 'Auction Held', icon: Hammer },
  { key: 'unit_cleared', label: 'Unit Cleared', icon: Package },
];

// Map a stored lien_stage key (e.g. "pre_lien") to its display label.
const STAGE_BY_KEY = Object.fromEntries(LIFECYCLE_STAGES.map((s) => [s.key, s]));
const stageLabel = (key) => STAGE_BY_KEY[key]?.label || 'Not started';

// Compute days late from a due-date string. Returns 0 if missing/invalid
// so the table doesn't render NaN.
const daysLateFrom = (dueDate) => {
  if (!dueDate) return 0;
  const due = new Date(dueDate);
  if (Number.isNaN(due.getTime())) return 0;
  const diff = Date.now() - due.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
};

const formatMoney = (n) => {
  const v = Number(n || 0);
  return `$${v.toFixed(2)}`;
};

const Auctions = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id;
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [view, setView] = useState('workflows'); // workflows (default) | delinquencies

  useEffect(() => {
    if (!facilityId) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Walk delinquent rentals for this facility. The customer-status
        // bucket is the source of truth for who's past due; we hydrate
        // each delinquent rental with the customer + unit so the table
        // has everything in one shot.
        const { data } = await apiClient.get(
          `/api/facilities/${facilityId}/rentals?status=Delinquent`,
        );
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.items || []);
        setRows(
          list.map((r) => ({
            id: r._id || r.id,
            customer_id: r.customer_id,
            customer_name: r.customer_name || r.customer?.full_name || '—',
            unit_number: r.unit_number || r.unit?.unit_number || '—',
            balance: r.balance || 0,
            due_date: r.due_date,
            days_late: daysLateFrom(r.due_date),
            // Current legal stage — written by the workflow engine as a lien
            // run advances through stage-mapped steps. Null until a run
            // reaches one (rendered as "Not started").
            lien_stage: r.lien_stage || null,
          })),
        );
      } catch (err) {
        if (cancelled) return;
        // Delinquent endpoint may not exist on every backend yet; fail
        // soft so the page still renders with an empty table.
        setError(
          err?.response?.status === 404
            ? null
            : (err?.response?.data?.detail || 'Failed to load delinquent rentals.'),
        );
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [facilityId]);

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-1">
          <Gavel className="h-5 w-5 text-slate-700" />
          <h1 className="text-xl font-semibold text-slate-900">Auctions <HelpLink slug="collections" /></h1>
        </div>
        <p className="text-[13px] text-slate-400">
          Manage delinquent rentals through lien notification, auction
          scheduling, and unit recovery.
        </p>
      </div>

      <div className="flex items-center gap-2 mb-5">
        {[['workflows', 'Workflows'], ['delinquencies', 'Delinquencies']].map(([k, lbl]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-3 py-1.5 rounded-lg text-[13px] font-medium ${view === k ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {view === 'workflows' ? <WorkflowBuilder /> : (
      <>

      {/* Lifecycle explainer: 5 stages, icon row, subtle. Tells the
          operator the cascade order so they understand the column
          values once the backend writes them. */}
      <div className="rounded-xl border border-slate-200/80 bg-white p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
          {LIFECYCLE_STAGES.map((stage, idx) => {
            const Icon = stage.icon;
            return (
              <React.Fragment key={stage.key}>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-50 border border-slate-200/80">
                  <Icon className="h-3.5 w-3.5 text-slate-500" />
                  <span className="font-medium text-slate-700">{stage.label}</span>
                </div>
                {idx < LIFECYCLE_STAGES.length - 1 && (
                  <span className="text-slate-300">&rarr;</span>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200/80 px-3 py-2.5 text-[13px] text-red-600">
          {error}
        </div>
      )}

      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-[14px] font-semibold text-slate-900">
            Delinquent rentals
          </h2>
          <span className="text-[12px] text-slate-400">
            {loading ? '...' : `${rows.length} row${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 flex flex-col items-center text-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-500 mb-2" />
            <p className="text-[13px] font-medium text-slate-700">
              No delinquent rentals. All accounts current.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] uppercase tracking-wider text-slate-400 bg-slate-50/50">
                <th className="px-5 py-2.5 font-medium">Customer</th>
                <th className="px-5 py-2.5 font-medium">Unit</th>
                <th className="px-5 py-2.5 font-medium">Days Late</th>
                <th className="px-5 py-2.5 font-medium">Balance</th>
                <th className="px-5 py-2.5 font-medium">Current Lien Stage</th>
                <th className="px-5 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-5 py-3 text-slate-900">{r.customer_name}</td>
                  <td className="px-5 py-3 text-slate-700">{r.unit_number}</td>
                  <td className="px-5 py-3 text-slate-700">{r.days_late}</td>
                  <td className="px-5 py-3 text-slate-700">{formatMoney(r.balance)}</td>
                  <td className="px-5 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                      r.lien_stage
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      {stageLabel(r.lien_stage)}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => setConfirmRow(r)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 transition-colors"
                    >
                      <Gavel className="h-3.5 w-3.5" /> Start lien process
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200/80 bg-white p-5 opacity-70 cursor-not-allowed">
          <div className="h-9 w-9 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
            <FileText className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="text-[15px] font-semibold text-slate-900 mb-1">
            Lien Notice Template
          </h3>
          <p className="text-[13px] text-slate-400 mb-3">
            Configure the certified-mail lien notice document here.
          </p>
          <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
            Coming Soon
          </span>
        </div>
        <div className="rounded-xl border border-slate-200/80 bg-white p-5 opacity-70 cursor-not-allowed">
          <div className="h-9 w-9 bg-blue-50 rounded-lg flex items-center justify-center mb-3">
            <Calendar className="h-4 w-4 text-blue-600" />
          </div>
          <h3 className="text-[15px] font-semibold text-slate-900 mb-1">
            Auction Schedule
          </h3>
          <p className="text-[13px] text-slate-400 mb-3">
            View and manage scheduled auctions.
          </p>
          <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">
            Coming Soon
          </span>
        </div>
      </div>

      {confirmRow && (
        <StartWorkflowModal
          facilityId={facilityId}
          row={confirmRow}
          onClose={() => setConfirmRow(null)}
          onStarted={() => { setConfirmRow(null); setView('workflows'); }}
        />
      )}
      </>
      )}
    </div>
  );
};

export default Auctions;
